import { useState } from 'react';
import {
  AirplaneTakeoffIcon,
  ArrowUpRightIcon,
  BedIcon,
  BusIcon,
  CarIcon,
  ChatCircleIcon,
  type Icon,
} from '@phosphor-icons/react';
import type { ChatUi, ConfirmPromptGlyph } from '@voll-intelligence/genui-contract';
import type { GenUiRenderProps } from '../../../shared/ui/registry.js';

type Block = Extract<ChatUi, { kind: 'confirm_prompt' }>;
type Action = 'confirm' | 'deny';

const GLYPHS: Record<ConfirmPromptGlyph, Icon> = {
  flight: AirplaneTakeoffIcon,
  hotel: BedIcon,
  rental: CarIcon,
  bus: BusIcon,
  generic: ChatCircleIcon,
};

/**
 * As duas respostas de uma pergunta do agente, no corpo do turno: a pergunta é o
 * texto do turno (o `message` só rotula o grupo para leitor de tela). Chips do
 * padrão de resposta do design (`sand`, nunca preenchimento de marca, que segue
 * reservado ao send e ao checkout). `readOnly` (respondido, card mais novo ou
 * conversa encerrada) e `superseded` (qualquer turno mais novo, mesmo sem card:
 * a pergunta perdeu o sentido) não renderizam: as opções somem e a escolha fica como a
 * bolha da pessoa. Cada botão dispara a `interaction` genérica com a decisão
 * DETERMINÍSTICA no `data` (anti-LLM); o rótulo tocado vira o balão do usuário.
 */
export function ConfirmPrompt({
  ui,
  readOnly,
  superseded,
  dispatch,
}: Readonly<GenUiRenderProps<Block>>) {
  // Trava local anti duplo-clique (a trava real — single-use — é server-side):
  // sem ela, um segundo toque no botão irmão mandaria uma `interaction`
  // contraditória pro MESMO challengeId enquanto o turno ainda está em voo.
  const [sent, setSent] = useState(false);
  if (readOnly || superseded) return null;
  const answer = (action: Action, text: string) => {
    setSent(true);
    dispatch({
      type: 'interaction',
      interactionId: ui.challengeId,
      text,
      data: { action },
      analytics: { actionId: action },
    });
  };
  return (
    // `cfp` não tem CSS: fica só como o localizador do e2e (`fieldset.cfp`).
    <fieldset
      className="cfp m-0 grid min-w-0 grid-cols-1 gap-2 border-0 p-0 sm:grid-cols-2"
      aria-label={ui.message}
    >
      <ReplyChip
        label={ui.denyLabel}
        hint={ui.denyHint}
        glyph={ui.denyGlyph ?? 'generic'}
        disabled={sent}
        onPick={() => {
          answer('deny', ui.denyLabel);
        }}
      />
      <ReplyChip
        label={ui.confirmLabel}
        hint={ui.confirmHint}
        glyph={ui.confirmGlyph ?? 'generic'}
        disabled={sent}
        onPick={() => {
          answer('confirm', ui.confirmLabel);
        }}
      />
    </fieldset>
  );
}

function ReplyChip({
  label,
  hint,
  glyph,
  disabled,
  onPick,
}: Readonly<{
  label: string;
  hint?: string;
  glyph: ConfirmPromptGlyph;
  disabled: boolean;
  onPick: () => void;
}>) {
  const Glyph = GLYPHS[glyph];
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPick}
      className="flex min-h-16 items-center gap-3 rounded-[20px] border border-border bg-sand-3 px-5 py-3.5 text-left text-foreground transition-colors outline-none hover:bg-sand-4 focus-visible:ring-3 focus-visible:ring-ring/50 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Glyph
        size={16}
        weight="bold"
        aria-hidden
        data-glyph={glyph}
        className="shrink-0 text-muted-foreground"
      />
      <span className="min-w-0 flex-1">
        <span className="text-detail block font-medium">{label}</span>
        {hint ? <span className="text-detail block">{hint}</span> : null}
      </span>
      <ArrowUpRightIcon
        size={16}
        weight="bold"
        aria-hidden
        className="shrink-0 text-muted-foreground"
      />
    </button>
  );
}
